
import { Component, OnInit, ViewEncapsulation, } from '@angular/core';
import { TranslationService } from '@alfresco/adf-core';
import {
  Constants as ConstantsShared,
  WorkflowService,
  ListTaskSort,
  SortDir,
  TaskListFilter,
  TaskListFilterDescription,
  TaskListFilterPriority,
  TaskListFilterInitiator,
  TaskListFilterDateStarted,
  TaskPriority,
  PageLayoutComponent,
  ToolbarComponent,
} from '@alfresco/aca-shared';
import { Constants } from '../constants';
import { Utils } from '../utils';
import { AppStore, getUserProfile, getUserList, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { Person } from '@alfresco/js-api';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MyTasksDialogComponent } from '../../../dialogs/my-tasks/my-tasks.dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    PageLayoutComponent,
    ToolbarComponent,
    MatProgressSpinnerModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
    MatTooltipModule,
  ],
  encapsulation: ViewEncapsulation.None,
  selector: 'aca-my-tasks',
  templateUrl: './my-tasks.component.html',
  styleUrls: ['./my-tasks.component.scss']
})
export class MyTasksComponent implements OnInit {

  actions = ConstantsShared.CUSTOM_PAGE_ACTIONS;

  totalTasksCount: number = 0;
  taskActive: string = 'active';
  currentSort: string = 'date';
  sortDateAsc: boolean = false;
  sortPriorityAsc: boolean = true;
  showFilters: boolean = false;
  priorityOptions: any[] = [];
  users: Person[] = [];
  usersFiltered: Observable<Person[]>;
  today = moment();

  filterForm = new FormGroup({
    description: new FormControl(''),
    priority: new FormControl('all'),
    dateStartedFrom: new FormControl(undefined),
    dateStartedTo: new FormControl(undefined),
    initiator: new FormControl(undefined),
  });
  userName: string;
  isLoading: boolean = false;

  currentPage: number = 1;
  selectedPage: number = 1;
  numberOfPages: number = 1;

  tasks: any[] = [];
  tasksPaginated: any[] = [];

  constructor(
    private store: Store<AppStore>,
    private translation: TranslationService,
    private workflowService: WorkflowService,
    private dialog: MatDialog,
    private router: Router,
  ) {
    this.priorityOptions = Utils.getPriorityOptions(this.translation);

    translation.translate.onLangChange.subscribe(() => {
      this.priorityOptions = Utils.getPriorityOptions(this.translation);
    });
  }

  ngOnInit() {
    this.isLoading = true;

    this.store.select(getUserProfile).subscribe((user) => {
      this.userName = user.id;
      this.onFormChange(null);
    });
    this.store.select(getUserList).subscribe((userList) => {
      this.users = userList;
    });
    
    this.usersFiltered = this.filterForm.get('initiator').valueChanges.pipe(
      startWith(''),
      map(value => this._filterUsers(value || '')),
    );
  }

  private _filterUsers(value: string): Person[] {
    if (!this.users)
      return [];
    const filterValue = value.toLowerCase();
    return this.users.filter(person => person.firstName.toLowerCase().includes(filterValue) || (person.lastName && person.lastName.toLowerCase().includes(filterValue)));
  }

  onFilterFormChange(event) {
    this.onFormChange(event);
  }

  loadData = async () => {
    if (!this.userName)
      return;

    this.isLoading = true;

    const filters: TaskListFilter[] = [];
    if (this.showFilters) {
      const description = this.filterForm.get('description').value;
      if (description && description.length >= 3) {
        const filter = new TaskListFilterDescription(description);
        filters.push(filter);
      }
      
      const priority = this.filterForm.get('priority').value;
      if (priority && priority !== 'all') {
        const enumVal = priority as TaskPriority;
        const filter = new TaskListFilterPriority(enumVal);
        filters.push(filter);
      }

      const startedFrom = this.filterForm.get('dateStartedFrom').value,
            startedTo = this.filterForm.get('dateStartedTo').value;
      if (startedFrom || startedTo) {
        const startedFromValue = startedFrom ? startedFrom : null;
        const startedToValue = startedTo ? startedTo : null;
        const filter = new TaskListFilterDateStarted(startedFromValue, startedToValue);
        filters.push(filter);
      }

      const initiator = this.filterForm.get('initiator').value;
      if (initiator) {
        const filter = new TaskListFilterInitiator(initiator);
        filters.push(filter);
      }
    }

    let sort: ListTaskSort,
        sortDir: SortDir;
    if (this.currentSort === 'date') {
      sort = ListTaskSort.DATE;
      sortDir = this.sortDateAsc ? SortDir.ASC : SortDir.DESC;
    } else if (this.currentSort === 'priority') {
      sort = ListTaskSort.PRIORITY;
      sortDir = this.sortPriorityAsc ? SortDir.ASC : SortDir.DESC;
    }

    await this.workflowService.listTasks(this.userName, this.taskActive === 'active', filters, sort, sortDir)
      .then((data) => {
        const tasks = data.data;
        if (!tasks) {
          this.store.dispatch(new SnackbarErrorAction(this.translation.instant('CORE.MESSAGES.ERRORS.GENERIC')));
          return;
        }

        this.tasks = tasks;
        this.totalTasksCount = tasks.length;
        this.numberOfPages = Math.floor(tasks.length / Constants.LIST_PAGE_ITEM_COUNT) + 1;
        if (tasks.length % Constants.LIST_PAGE_ITEM_COUNT === 0)
          this.numberOfPages--;

        this.isLoading = false;
      })
      .catch((_) => {
        this.store.dispatch(new SnackbarErrorAction(this.translation.instant('CORE.MESSAGES.ERRORS.GENERIC')));
      });
    this.currentPage = this.selectedPage = 1;
    this.paginate();
  }

  onFormChange(event) {
    if (event?.value) this.selectedPage = this.currentPage = 1;
    this.loadData();
  }

  onFormEnter(event) {
    event.preventDefault();
    this.onFormChange(event);
  }

  onSortByDate(event) {
    if (this.currentSort !== 'date') {
      this.currentSort = 'date';
    } else {
      this.sortDateAsc = !this.sortDateAsc;
    }
    this.onFormChange(event);
  }

  onSortByPriority(event) {
    if (this.currentSort !== 'priority') {
      this.currentSort = 'priority';
    } else {
      this.sortPriorityAsc = !this.sortPriorityAsc;
    }
    this.onFormChange(event);
  }

  onToggleShowFilters() {
    this.showFilters = !this.showFilters;
  }

  onResetFilters(_) {
    this.filterForm.reset();
    this.filterForm.get('priority').setValue('all');
    this.onFormChange(null);
  }

  getTaskIconDetails(task: object): { icon: string, color: string } {
    const statusCondition = this.checkTaskStatusCondition(task);
    

    const taskConditions = [
      {
        condition: statusCondition && this.checkTaskCommentCondition(task), 
        icon: 'comment',
        color: 'circle-yellow'
      },
      {
        condition: statusCondition,
        icon: 'task_alt',
        color: 'circle-green'
      },
      {
        condition: true,
        icon: 'timelapse',
        color: 'circle-red'
      }
    ];
  
    const matchingCondition = taskConditions.find(item => item.condition);
    return matchingCondition ? { icon: matchingCondition.icon, color: matchingCondition.color } : { icon: 'timelapse', color: 'circle-red' };
  }

  getTaskSentValue(task: object) {
    const isoString = task['workflowInstance']['startDate'];
    return Utils.getSentStringValue(isoString, this.translation);
  }

  getTaskFromValue(task: object) {
    const initiator = task['workflowInstance']['initiator'];
    return `${initiator.firstName}${initiator.lastName ? (' ' + initiator.lastName) : ''}`;
  }

  getTaskPriorityValue(task: object) {
    return Utils.getPriorityTranslation(this.translation, task['properties']['bpm_priority']);
  }

  getTaskTypeValue(task: object) {
    const taskTypeName = Utils.getTaskTypeName(task['name'], this.translation);
    const isPooled = task['isPooled'];

    let icon = '';
    if (taskTypeName === this.translation.instant('APP.WORKFLOW.GENERAL.TASK_TYPE.REVIEW') && !isPooled) {
      icon = 'manage_search';
    }
    if (taskTypeName === this.translation.instant('APP.WORKFLOW.GENERAL.TASK_TYPE.APPROVE_REJECT')) {
      icon = 'task_alt';
    }

    return { type: taskTypeName, icon: icon };
  }

  getTaskDueValue(task: object) {
    const dueDateIsoString = task['properties']['bpm_dueDate'];
    if (dueDateIsoString) {
      const dueDate = Utils.dateFromISO8601(dueDateIsoString);
      return Utils.formatDate(dueDate);
    } else {
      return '/';
    }
  }

  onClickTask(taskId: string) {
    const taskIdShort = taskId.substring('activiti$'.length);
    const url = `/workflow/task-details/${taskIdShort}`;
    this.router.navigateByUrl(url);
    // const url = `#/workflow/task-details/${taskIdShort}`;
    // window.open(url, '_blank');
  }

  onJumpToPage() {
    if (this.selectedPage >= 1 && this.selectedPage <= this.numberOfPages) {
      this.currentPage = this.selectedPage;
      this.paginate();
    } else {
      this.selectedPage = this.currentPage;
    }
  }

  onNextPage() {
    if (this.currentPage < this.numberOfPages) {
      this.currentPage++;
      this.selectedPage = this.currentPage;
      this.paginate();
    }
  }

  onPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.selectedPage = this.currentPage;
      this.paginate();
    }
  }

  paginate() {
    this.tasksPaginated = this.tasks.slice((this.currentPage - 1) * Constants.LIST_PAGE_ITEM_COUNT, this.currentPage * Constants.LIST_PAGE_ITEM_COUNT);
  }

  openMyTasksDialog(): void {
    const dialogRef = this.dialog.open(MyTasksDialogComponent, {
      width: '450px',
      restoreFocus: true,
      data: {
        tasks: this.tasks
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'refresh') {
        this.onFormChange({ value: 'active' });
      }
    });
  }

  checkTaskStatusCondition(task: any): boolean {
    const statusConditionOutcomes = [
      "Task Completed",
      "Approved",
      "Rejected",
      "Document Rejected",
      "Site Invitation Accepted",
      "Site Invitation Rejected",
      "Завршена Задача",
      "Отфрлено",
      "Одобрено",
      "Поканата за Сајт е Прифатена",
      "Поканата за Сајт е Одбиена",
      "Покана да се Придружите на Проектот",
      "Откажана Покана за Сајт"
    ];
    return statusConditionOutcomes.includes(task['title'] || '');
  }

  checkTaskCommentCondition(task: any): boolean {
    const comment1 = task['properties']['ncwf_viewApprovers'],
      comment2 = task['properties']['ncwf_viewRejectors'],
      commentPGR = task['properties']['ncwf_reviewersComments'];
    return this.checkReviewComment(commentPGR) || this.checkReviewComment(comment1) || this.checkReviewComment(comment2);
  }

  private checkReviewComment(comment: string) {
    if(comment == null) return false;
    if(comment == "") return false;
    if(comment == "null") return false;
    if(comment.indexOf("%*/.stop") == -1) return false;
    var control = "";
    var array = comment.split("%*/.stop");
    for(var i in array) {
        var item = array[i];
    if(!item) continue;
        if(item == "") continue;
    if(item == "null") continue;
        var comment = item.split("-")[1].trim();
        if(comment != "")
          control+=comment;
    }
    if(control == "") return false;
    return true;
  }
}
